import React, { useState, useEffect, useMemo } from 'react';
import logger from '../lib/logger';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { testAPI, questionAPI, bookmarkAPI } from '../lib/api';
import { renderLatex } from '../lib/renderLatex';
import { 
  Clock, ChevronLeft, ChevronRight, Flag, CheckCircle2, AlertCircle, 
  XCircle, BookOpen, Eye, Target, FileText, Bookmark,
  Circle, CircleDot, CircleOff, HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';

// Question Status Constants
const STATUS = {
  NOT_VISITED: 'not_visited',
  NOT_ANSWERED: 'not_answered',
  ANSWERED: 'answered',
  MARKED_FOR_REVIEW: 'marked_review',
  ANSWERED_AND_MARKED: 'answered_marked'
};

const NTATest = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  // Core Test State
  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(null);
  
  // Organized by Subject and Section
  const [subjects, setSubjects] = useState([]);
  const [currentSubject, setCurrentSubject] = useState(null);
  const [currentSection, setCurrentSection] = useState('A'); // A = MCQ, B = Numerical
  
  // Question State
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [questionStatus, setQuestionStatus] = useState({});
  const [bookmarked, setBookmarked] = useState(new Set());
  
  // UI State
  const [showInstructions, setShowInstructions] = useState(false);
  const [showQuestionPaper, setShowQuestionPaper] = useState(false);
  const [examConfig, setExamConfig] = useState(null);
  const [timeExpired, setTimeExpired] = useState(false);
  
  // Timing and Analytics
  const [startTime] = useState(Date.now());
  const [questionTiming, setQuestionTiming] = useState({});
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [visitCount, setVisitCount] = useState({}); // Tracks how many times each question was visited
  const [originalAnswers, setOriginalAnswers] = useState({}); // First answer before any changes
  const [skippedInitially, setSkippedInitially] = useState(new Set()); // Questions that were skipped then returned

  // Load test data
  useEffect(() => {
    loadTest();
  }, [testId]);

  // Timer
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleAutoSubmit();
          return 0;
        }
        if (prev === 300) {
          toast.warning('⏰ 5 minutes remaining!');
        }
        if (prev === 60) {
          toast.warning('⚠️ 1 minute left!');
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const loadTest = async () => {
    try {
      const testRes = await testAPI.getById(testId);
      setTest(testRes.data);

      // Load questions with error handling - skip missing questions
      const allQuestions = [];
      for (const qId of testRes.data.questions) {
        try {
          const qRes = await questionAPI.getById(qId);
          allQuestions.push(qRes.data);
        } catch (err) {
          // Question not found, skipping
        }
      }
      
      if (allQuestions.length === 0) {
        toast.error('No valid questions found for this test');
        navigate('/exams');
        return;
      }
      
      // Organize questions by subject
      const subjectMap = {};
      allQuestions.forEach((q, idx) => {
        const subject = q.subject_name || 'General';
        if (!subjectMap[subject]) {
          subjectMap[subject] = {
            name: subject,
            sectionA: [], // MCQs
            sectionB: []  // Numerical
          };
        }
        
        // Determine question type
        const isNumerical = q.question_type === 'Numerical' || 
                          q.question_type === 'Integer' || 
                          !q.options || 
                          q.options.length === 0;
        
        if (isNumerical) {
          subjectMap[subject].sectionB.push({ ...q, originalIndex: idx });
        } else {
          subjectMap[subject].sectionA.push({ ...q, originalIndex: idx });
        }
      });

      // Convert to array and set default subject order
      const subjectOrder = ['Physics', 'Chemistry', 'Mathematics', 'Biology', 'General'];
      const sortedSubjects = Object.values(subjectMap).sort((a, b) => {
        const idxA = subjectOrder.indexOf(a.name);
        const idxB = subjectOrder.indexOf(b.name);
        return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
      });

      setSubjects(sortedSubjects);
      setQuestions(allQuestions);
      
      if (sortedSubjects.length > 0) {
        setCurrentSubject(sortedSubjects[0].name);
      }

      // Set initial status for all questions
      const initialStatus = {};
      allQuestions.forEach((q) => {
        initialStatus[q.id] = STATUS.NOT_VISITED;
      });
      setQuestionStatus(initialStatus);

      // Calculate time
      let timeInMinutes = 60;
      if (testRes.data.exam_config?.duration_minutes) {
        timeInMinutes = testRes.data.exam_config.duration_minutes;
      } else if (testRes.data.duration) {
        timeInMinutes = testRes.data.duration;
      } else {
        timeInMinutes = Math.max(allQuestions.length * 2, 30);
      }
      
      setTimeLeft(timeInMinutes * 60);
      setExamConfig(testRes.data.exam_config);
      setQuestionStartTime(Date.now());

    } catch (error) {
      logger.error('Error loading test:', error);
      toast.error('Failed to load test');
      navigate('/exams');
    } finally {
      setLoading(false);
    }
  };

  // Get current subject's questions
  const currentSubjectData = useMemo(() => {
    return subjects.find(s => s.name === currentSubject) || { sectionA: [], sectionB: [] };
  }, [subjects, currentSubject]);

  // Get current section's questions
  const currentSectionQuestions = useMemo(() => {
    return currentSection === 'A' ? currentSubjectData.sectionA : currentSubjectData.sectionB;
  }, [currentSubjectData, currentSection]);

  // Get current question
  const currentQuestion = currentSectionQuestions[currentQuestionIndex];

  // Handle answer selection
  const handleAnswerSelect = (answer) => {
    if (!currentQuestion || timeExpired) return;

    const qId = currentQuestion.id;
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    
    setQuestionTiming(prev => ({ ...prev, [qId]: (prev[qId] || 0) + timeSpent }));
    
    // Track original answer (first answer before any changes)
    if (!originalAnswers[qId] && !answers[qId]) {
      setOriginalAnswers(prev => ({ ...prev, [qId]: answer }));
    }
    
    setAnswers(prev => ({ ...prev, [qId]: answer }));
    
    // Update status
    const currentStatus = questionStatus[qId];
    if (currentStatus === STATUS.MARKED_FOR_REVIEW || currentStatus === STATUS.ANSWERED_AND_MARKED) {
      setQuestionStatus(prev => ({ ...prev, [qId]: STATUS.ANSWERED_AND_MARKED }));
    } else {
      setQuestionStatus(prev => ({ ...prev, [qId]: STATUS.ANSWERED }));
    }
    
    setQuestionStartTime(Date.now());
  };

  // Clear response
  const handleClearResponse = () => {
    if (!currentQuestion) return;
    const qId = currentQuestion.id;
    
    const newAnswers = { ...answers };
    delete newAnswers[qId];
    setAnswers(newAnswers);
    
    const currentStatus = questionStatus[qId];
    if (currentStatus === STATUS.ANSWERED_AND_MARKED) {
      setQuestionStatus(prev => ({ ...prev, [qId]: STATUS.MARKED_FOR_REVIEW }));
    } else {
      setQuestionStatus(prev => ({ ...prev, [qId]: STATUS.NOT_ANSWERED }));
    }
  };

  // Mark for review
  const handleMarkForReview = () => {
    if (!currentQuestion) return;
    const qId = currentQuestion.id;
    
    const hasAnswer = answers[qId] !== undefined;
    setQuestionStatus(prev => ({
      ...prev,
      [qId]: hasAnswer ? STATUS.ANSWERED_AND_MARKED : STATUS.MARKED_FOR_REVIEW
    }));
  };

  // Toggle bookmark
  const toggleBookmark = async () => {
    const questionId = currentQuestion?.id;
    if (!questionId) return;
    
    try {
      const newBookmarked = new Set(bookmarked);
      if (newBookmarked.has(questionId)) {
        await bookmarkAPI.remove(questionId);
        newBookmarked.delete(questionId);
        toast.success('Bookmark removed');
      } else {
        await bookmarkAPI.add(questionId);
        newBookmarked.add(questionId);
        toast.success('Question bookmarked');
      }
      setBookmarked(newBookmarked);
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  };

  // Save and Next
  const handleSaveAndNext = () => {
    if (!currentQuestion) return;
    
    const qId = currentQuestion.id;
    const currentStatus = questionStatus[qId];
    
    // Mark as visited if not already
    if (currentStatus === STATUS.NOT_VISITED) {
      const hasAnswer = answers[qId] !== undefined;
      setQuestionStatus(prev => ({
        ...prev,
        [qId]: hasAnswer ? STATUS.ANSWERED : STATUS.NOT_ANSWERED
      }));
    }
    
    // Navigate to next question
    if (currentQuestionIndex < currentSectionQuestions.length - 1) {
      navigateToQuestion(currentQuestionIndex + 1);
    } else if (currentSection === 'A' && currentSubjectData.sectionB.length > 0) {
      // Move to section B
      setCurrentSection('B');
      setCurrentQuestionIndex(0);
      markQuestionVisited(currentSubjectData.sectionB[0]?.id);
    } else {
      // Move to next subject
      const currentSubjectIdx = subjects.findIndex(s => s.name === currentSubject);
      if (currentSubjectIdx < subjects.length - 1) {
        const nextSubject = subjects[currentSubjectIdx + 1];
        setCurrentSubject(nextSubject.name);
        setCurrentSection('A');
        setCurrentQuestionIndex(0);
        markQuestionVisited(nextSubject.sectionA[0]?.id);
      }
    }
  };

  // Mark as visited and track analytics
  const markQuestionVisited = (qId) => {
    if (!qId) return;
    
    // Track time on previous question
    if (currentQuestion) {
      const prevQId = currentQuestion.id;
      const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
      setQuestionTiming(prev => ({ ...prev, [prevQId]: (prev[prevQId] || 0) + timeSpent }));
      
      // Mark as skipped if leaving without answering
      if (!answers[prevQId]) {
        setSkippedInitially(prev => new Set([...prev, prevQId]));
      }
    }
    
    // Track visit count for the new question
    setVisitCount(prev => ({ ...prev, [qId]: (prev[qId] || 0) + 1 }));
    
    if (questionStatus[qId] === STATUS.NOT_VISITED) {
      setQuestionStatus(prev => ({ ...prev, [qId]: STATUS.NOT_ANSWERED }));
    }
    setQuestionStartTime(Date.now());
  };

  // Navigate to specific question
  const navigateToQuestion = (index) => {
    setCurrentQuestionIndex(index);
    const q = currentSectionQuestions[index];
    if (q) {
      markQuestionVisited(q.id);
    }
  };

  // Switch subject
  const switchSubject = (subjectName) => {
    setCurrentSubject(subjectName);
    setCurrentSection('A');
    setCurrentQuestionIndex(0);
    const subjectData = subjects.find(s => s.name === subjectName);
    if (subjectData?.sectionA[0]) {
      markQuestionVisited(subjectData.sectionA[0].id);
    }
  };

  // Format time
  const formatTime = (seconds) => {
    if (seconds === null) return '--:--:--';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get status counts
  const getStatusCounts = () => {
    const counts = {
      answered: 0,
      notAnswered: 0,
      markedReview: 0,
      answeredMarked: 0,
      notVisited: 0
    };
    
    Object.values(questionStatus).forEach(status => {
      switch(status) {
        case STATUS.ANSWERED: counts.answered++; break;
        case STATUS.NOT_ANSWERED: counts.notAnswered++; break;
        case STATUS.MARKED_FOR_REVIEW: counts.markedReview++; break;
        case STATUS.ANSWERED_AND_MARKED: counts.answeredMarked++; break;
        case STATUS.NOT_VISITED: counts.notVisited++; break;
      }
    });
    
    return counts;
  };

  // Core submission logic — no confirmation dialog
  const doSubmit = async () => {
    // Calculate total time
    const totalTimeSeconds = Math.floor((Date.now() - startTime) / 1000);

    // Build enhanced answer data for advanced analytics
    const formattedAnswers = questions.map((q, idx) => {
      const qId = q.id;
      const selectedAnswer = answers[qId] || null;
      const originalAnswer = originalAnswers[qId];
      const wasAnswerChanged = originalAnswer && originalAnswer !== selectedAnswer;
      const status = questionStatus[qId];
      const isMarked = status === STATUS.MARKED_FOR_REVIEW || status === STATUS.ANSWERED_AND_MARKED;
      
      return {
        question_id: qId,
        selected_answer: selectedAnswer,
        time_spent_seconds: questionTiming[qId] || 0,
        times_visited: visitCount[qId] || 1,
        answer_changed: wasAnswerChanged,
        original_answer: wasAnswerChanged ? originalAnswer : null,
        was_marked_for_review: isMarked,
        was_skipped_initially: skippedInitially.has(qId),
        question_position: idx + 1,
      };
    });

    try {
      await testAPI.submit(testId, formattedAnswers, totalTimeSeconds);
      toast.success('🎉 Test submitted successfully!');
      navigate(`/test/${testId}/results`);
    } catch (error) {
      toast.error('Failed to submit test');
    }
  };

  // Submit test — shows confirmation when there are unanswered questions
  const handleSubmit = async () => {
    const counts = getStatusCounts();
    const unanswered = questions.length - counts.answered - counts.answeredMarked;
    
    if (unanswered > 0) {
      const confirm = window.confirm(
        `You have ${unanswered} unanswered questions. Are you sure you want to submit?`
      );
      if (!confirm) return;
    }

    await doSubmit();
  };

  // Auto-submit when timer expires — no confirmation, no interaction allowed
  const handleAutoSubmit = () => {
    setTimeExpired(true);
    toast.error("⏰ Time's up! Test is being submitted automatically...");
    doSubmit();
  };

  // Get status color for question palette
  const getStatusColor = (qId) => {
    const status = questionStatus[qId];
    switch(status) {
      case STATUS.ANSWERED:
        return 'bg-green-500 text-white border-green-600';
      case STATUS.NOT_ANSWERED:
        return 'bg-red-500 text-white border-red-600';
      case STATUS.MARKED_FOR_REVIEW:
        return 'bg-purple-500 text-white border-purple-600';
      case STATUS.ANSWERED_AND_MARKED:
        return 'bg-purple-500 text-white border-purple-600 ring-2 ring-green-400';
      case STATUS.NOT_VISITED:
      default:
        return 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600';
    }
  };

  if (loading) {
    return <LoadingSpinner size="xl" text="Loading your test..." fullScreen={true} />;
  }

  if (!test || questions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="text-center text-red-500">
          <AlertCircle size={48} className="mx-auto mb-4" />
          <p>Failed to load test</p>
        </div>
      </div>
    );
  }

  const statusCounts = getStatusCounts();

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex flex-col">
      {/* NTA Style Header */}
      <header className="bg-blue-800 text-white">
        <div className="flex items-center justify-between px-4 py-2 border-b border-blue-700">
          <div className="flex items-center gap-4">
            {/* ExamDex Branding */}
            <div className="flex items-center gap-2">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center overflow-hidden">
                <img src="/logo192.png" alt="ExamDex" className="w-10 h-10" />
              </div>
              <span className="text-sm font-bold text-white/90 hidden sm:inline">ExamDex</span>
            </div>
            <div className="h-6 w-px bg-blue-600 hidden sm:block"></div>
            <div className="text-lg font-bold">
              {test?.exam_name || 'Online Test'}
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 bg-blue-700 px-4 py-2 rounded">
              <Clock size={20} />
              <span className="font-mono text-xl font-bold">{formatTime(timeLeft)}</span>
            </div>
          </div>
        </div>
        
        {/* Subject Tabs */}
        <div className="flex items-center gap-1 px-4 py-2 bg-blue-700 overflow-x-auto">
          {subjects.map((subject) => (
            <button
              key={subject.name}
              onClick={() => switchSubject(subject.name)}
              className={`px-6 py-2 rounded-t font-medium transition-all whitespace-nowrap ${
                currentSubject === subject.name
                  ? 'bg-white text-blue-800'
                  : 'bg-blue-600 text-white hover:bg-blue-500'
              }`}
            >
              {subject.name}
            </button>
          ))}
        </div>
      </header>

      {/* Section Tabs */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-300 dark:border-gray-700 px-4 py-2 flex gap-4">
        {currentSubjectData.sectionA.length > 0 && (
          <button
            onClick={() => { setCurrentSection('A'); setCurrentQuestionIndex(0); }}
            className={`px-6 py-2 rounded font-medium ${
              currentSection === 'A'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Section A - MCQ ({currentSubjectData.sectionA.length})
          </button>
        )}
        {currentSubjectData.sectionB.length > 0 && (
          <button
            onClick={() => { setCurrentSection('B'); setCurrentQuestionIndex(0); }}
            className={`px-6 py-2 rounded font-medium ${
              currentSection === 'B'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Section B - Numerical ({currentSubjectData.sectionB.length})
          </button>
        )}
        
        <div className="ml-auto flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <span>Marking: {currentSection === 'A' ? '+4, -1' : '+4, 0'}</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Question Panel */}
        <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 border-r border-gray-300 dark:border-gray-700">
          {/* Question Header */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded font-medium">
                  Question {currentQuestionIndex + 1} of {currentSectionQuestions.length}
                </span>
                <span className={`px-3 py-1 rounded text-sm font-medium ${
                  currentSection === 'A' 
                    ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                    : 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
                }`}>
                  {currentSection === 'A' ? 'Multiple Choice' : 'Numerical'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={toggleBookmark}
                  variant="outline"
                  size="sm"
                  className={`gap-2 ${bookmarked.has(currentQuestion?.id) ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700' : ''}`}
                >
                  <Bookmark size={16} fill={bookmarked.has(currentQuestion?.id) ? 'currentColor' : 'none'} />
                  {bookmarked.has(currentQuestion?.id) ? 'Bookmarked' : 'Bookmark'}
                </Button>
              </div>
            </div>
          </div>

          {/* Question Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {currentQuestion ? (
              <div className="max-w-4xl mx-auto">
                {/* Question Text */}
                <div className="mb-8">
                  <div 
                    className="text-lg font-medium text-gray-900 dark:text-gray-100 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: renderLatex(currentQuestion.question_text || '') }}
                  />
                  {/* Diagram / question image */}
                  {currentQuestion.image_url && (
                    <div className="mt-4" id={`qimg-${currentQuestion.id}`}>
                      <img
                        src={currentQuestion.image_url}
                        alt=""
                        className="max-w-full rounded-lg border border-gray-200 dark:border-gray-700"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const wrapper = document.getElementById(`qimg-${currentQuestion.id}`);
                          if (wrapper) wrapper.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Answer Options */}
                {currentSection === 'A' && currentQuestion.options ? (
                  <div className="space-y-3">
                    {currentQuestion.options.map((option, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleAnswerSelect(option.label)}
                        className={`w-full p-4 rounded-lg border-2 text-left transition-all flex items-start gap-4 ${
                          answers[currentQuestion.id] === option.label
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-blue-300'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                          answers[currentQuestion.id] === option.label
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}>
                          {option.label}
                        </div>
                        <span 
                          className="pt-1 text-gray-800 dark:text-gray-200"
                          dangerouslySetInnerHTML={{ __html: renderLatex(option.text || '') }}
                        />
                      </button>
                    ))}
                  </div>
                ) : (
                  /* Numerical Input */
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-purple-600 dark:text-purple-400">
                      Enter your numerical answer:
                    </label>
                    <Input
                      type="number"
                      step="any"
                      value={answers[currentQuestion.id] || ''}
                      onChange={(e) => handleAnswerSelect(e.target.value)}
                      placeholder="Enter value (up to 2 decimal places)"
                      className="w-full max-w-md text-lg p-4 text-center font-mono bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-300 dark:border-purple-700 rounded-lg focus:border-purple-500"
                    />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Note: Answer should be rounded to 2 decimal places. No negative marking for wrong answers.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No questions in this section
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between">
            <div className="flex gap-3">
              <Button
                onClick={handleMarkForReview}
                variant="outline"
                className="bg-purple-50 dark:bg-purple-900/20 border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/30"
              >
                <Flag size={16} className="mr-2" />
                Mark for Review & Next
              </Button>
              <Button
                onClick={handleClearResponse}
                variant="outline"
                className="bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/30"
              >
                Clear Response
              </Button>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => currentQuestionIndex > 0 && navigateToQuestion(currentQuestionIndex - 1)}
                disabled={currentQuestionIndex === 0}
                variant="outline"
              >
                <ChevronLeft size={16} className="mr-1" />
                Previous
              </Button>
              <Button
                onClick={handleSaveAndNext}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Save & Next
                <ChevronRight size={16} className="ml-1" />
              </Button>
            </div>
          </div>
        </div>

        {/* Right Panel - Question Palette */}
        <div className="w-80 bg-gray-50 dark:bg-gray-900 flex flex-col overflow-hidden">
          {/* User Info */}
          <div className="p-3 bg-blue-800 text-white text-center font-medium">
            Candidate: Student
          </div>

          {/* Legend */}
          <div className="p-3 border-b border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-green-500 border border-green-600"></div>
                <span>Answered</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-red-500 border border-red-600"></div>
                <span>Not Answered</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-gray-200 dark:bg-gray-700 border border-gray-300 dark:border-gray-600"></div>
                <span>Not Visited</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-purple-500 border border-purple-600"></div>
                <span>Marked for Review</span>
              </div>
              <div className="flex items-center gap-2 col-span-2">
                <div className="w-6 h-6 rounded bg-purple-500 border border-purple-600 ring-2 ring-green-400"></div>
                <span>Answered & Marked for Review</span>
              </div>
            </div>
          </div>

          {/* Status Summary */}
          <div className="p-3 border-b border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-lg font-bold text-green-600">{statusCounts.answered + statusCounts.answeredMarked}</div>
                <div className="text-xs text-gray-500">Answered</div>
              </div>
              <div>
                <div className="text-lg font-bold text-red-600">{statusCounts.notAnswered}</div>
                <div className="text-xs text-gray-500">Not Ans.</div>
              </div>
              <div>
                <div className="text-lg font-bold text-purple-600">{statusCounts.markedReview + statusCounts.answeredMarked}</div>
                <div className="text-xs text-gray-500">Marked</div>
              </div>
            </div>
          </div>

          {/* Question Palette */}
          <div className="flex-1 overflow-y-auto p-3">
            {subjects.map((subject) => (
              <div key={subject.name} className="mb-4">
                <h4 className={`font-medium mb-2 text-sm ${
                  subject.name === currentSubject 
                    ? 'text-blue-600 dark:text-blue-400' 
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {subject.name}
                </h4>
                
                {/* Section A Questions */}
                {subject.sectionA.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-1">Section A (MCQ)</p>
                    <div className="grid grid-cols-5 gap-1">
                      {subject.sectionA.map((q, idx) => (
                        <button
                          key={q.id}
                          onClick={() => {
                            setCurrentSubject(subject.name);
                            setCurrentSection('A');
                            setCurrentQuestionIndex(idx);
                            markQuestionVisited(q.id);
                          }}
                          className={`w-8 h-8 rounded text-xs font-medium border transition-all ${
                            subject.name === currentSubject && currentSection === 'A' && currentQuestionIndex === idx
                              ? 'ring-2 ring-blue-400 ring-offset-1'
                              : ''
                          } ${getStatusColor(q.id)}`}
                        >
                          {idx + 1}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Section B Questions */}
                {subject.sectionB.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Section B (Numerical)</p>
                    <div className="grid grid-cols-5 gap-1">
                      {subject.sectionB.map((q, idx) => (
                        <button
                          key={q.id}
                          onClick={() => {
                            setCurrentSubject(subject.name);
                            setCurrentSection('B');
                            setCurrentQuestionIndex(idx);
                            markQuestionVisited(q.id);
                          }}
                          className={`w-8 h-8 rounded text-xs font-medium border transition-all ${
                            subject.name === currentSubject && currentSection === 'B' && currentQuestionIndex === idx
                              ? 'ring-2 ring-blue-400 ring-offset-1'
                              : ''
                          } ${getStatusColor(q.id)}`}
                        >
                          {idx + 1}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Submit Button */}
          <div className="p-3 border-t border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800">
            <Button
              onClick={handleSubmit}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3"
            >
              <CheckCircle2 size={18} className="mr-2" />
              Submit Test
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NTATest;
